import React, { useState, useEffect, useRef, useCallback } from 'react';

/* ────────────────────────────────────────────────────────────
   Main Component: HeroRotatingCard
   Tilted 3D card deck that flips Y-axis 180° to swap templates,
   featuring double-sided cards (Front and Back) so it never
   goes blank, along with original size dimensions.
   ──────────────────────────────────────────────────────────── */
export default function HeroRotatingCard() {
  const [frontIdx, setFrontIdx] = useState(0);
  const [backIdx, setBackIdx] = useState(1);
  const [isFrontActive, setIsFrontActive] = useState(true);
  const [activeIdx, setActiveIdx] = useState(0); // tracks current dot index (0 to 3)
  const [rotation, setRotation] = useState(0); // tracks cumulative Y-rotation
  const [isSpinning, setIsSpinning] = useState(false); // track if spinning to fade overlays
  const timerRef = useRef(null);
  const HOLD_MS = 5500; // time to display each state
  const SPIN_MS = 1500; // duration of the flip animation (slower, smoother)

  const spinToNext = useCallback(() => {
    setIsSpinning(true);
    // 1. Rotate by 180 degrees
    setRotation(prev => prev + 180);
    
    // 2. Calculate next index
    const nextIdx = (activeIdx + 1) % 4;
    setActiveIdx(nextIdx);

    // 3. Determine if Front or Back will face the user next
    const nextIsFrontActive = !isFrontActive;
    setIsFrontActive(nextIsFrontActive);

    // 4. Load content onto the side rotating into view,
    // then swap the inactive hidden side after the flip completes.
    if (nextIsFrontActive) {
      setFrontIdx(nextIdx);
      setTimeout(() => {
        setBackIdx((nextIdx + 1) % 4);
        setIsSpinning(false);
      }, SPIN_MS);
    } else {
      setBackIdx(nextIdx);
      setTimeout(() => {
        setFrontIdx((nextIdx + 1) % 4);
        setIsSpinning(false);
      }, SPIN_MS);
    }

  }, [activeIdx, isFrontActive]);

  // Set up the automatic rotation loop
  useEffect(() => {
    timerRef.current = setInterval(spinToNext, HOLD_MS);
    return () => clearInterval(timerRef.current);
  }, [spinToNext]);

  const handleDotClick = (idx) => {
    if (idx === activeIdx || isSpinning) return;
    clearInterval(timerRef.current);
    
    setIsSpinning(true);
    setRotation(prev => prev + 180);
    
    const nextIsFrontActive = !isFrontActive;
    setIsFrontActive(nextIsFrontActive);
    
    if (nextIsFrontActive) {
      setFrontIdx(idx);
      setTimeout(() => {
        setBackIdx((idx + 1) % 4);
        setIsSpinning(false);
      }, SPIN_MS);
    } else {
      setBackIdx(idx);
      setTimeout(() => {
        setFrontIdx((idx + 1) % 4);
        setIsSpinning(false);
      }, SPIN_MS);
    }
    
    setActiveIdx(idx);
    timerRef.current = setInterval(spinToNext, HOLD_MS);
  };

  // Helper to render the resume templates and their overlays
  const renderCardContent = (idx) => {
    return (
      <div className="flex-1 flex flex-col justify-between text-left h-full relative">
        {/* ── TEMPLATE 0: ATS CHECK (Arjun Sharma) ── */}
        {idx === 0 && (
          <>
            <div className="flex-1 flex flex-col justify-between text-left">
              {/* Header */}
              <div className="border-b border-gray-100 pb-2">
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center text-[#FCA311] font-bold text-xs">
                    AS
                  </div>
                  <div>
                    <h3 className="text-[12.5px] font-black text-gray-800 tracking-tight leading-tight">Arjun Sharma</h3>
                    <div className="text-[8.5px] text-gray-400 font-bold uppercase tracking-wider mt-0.5">Senior Software Engineer</div>
                  </div>
                </div>
              </div>

              {/* Body */}
              <div className="flex-1 mt-3 space-y-3">
                {/* Summary */}
                <div>
                  <span className="text-[8px] font-extrabold uppercase tracking-widest text-[#FCA311]">Profile Summary</span>
                  <p className="text-[9px] text-gray-600 leading-normal mt-0.5">
                    Full-stack developer with 5+ years experience building React and Node.js products. Specialized in database query optimization and highly scalable cloud architectures.
                  </p>
                </div>

                {/* Experience */}
                <div className="border-t border-gray-50 pt-2">
                  <span className="text-[8px] font-extrabold uppercase tracking-widest text-[#FCA311]">Work Experience</span>
                  <div className="mt-1">
                    <div className="flex justify-between items-center text-[9px] font-extrabold text-gray-850">
                      <span>Senior Engineer · Infosys Limited</span>
                      <span className="text-gray-400 font-medium text-[8px]">2022 - Present</span>
                    </div>
                    <ul className="list-disc list-inside text-[8.5px] text-gray-600 space-y-0.5 mt-1 leading-normal">
                      <li>Architected React + TypeScript frontend, increasing speed by 55%.</li>
                      <li>Built microservices serving 2M+ active users, reducing lag by 40%.</li>
                    </ul>
                  </div>
                </div>

                {/* Keyword check */}
                <div className="border-t border-dashed border-gray-100 pt-2">
                  <span className="text-[8px] font-extrabold uppercase tracking-widest text-gray-400">Keyword Check</span>
                  <div className="grid grid-cols-2 gap-x-2 gap-y-0.5 mt-1">
                    {[
                      { name: 'React & Node.js', match: true },
                      { name: 'Python backend', match: true },
                      { name: 'API Scalability', match: true },
                      { name: 'AWS Cloud Services', match: false },
                    ].map((s, i) => (
                      <div key={i} className="flex items-center gap-1">
                        <span className={`text-[9.5px] ${s.match ? 'text-emerald-500 font-bold' : 'text-amber-500 font-bold'}`}>
                          {s.match ? '✓' : '✦'}
                        </span>
                        <span className={`text-[8.5px] font-medium ${s.match ? 'text-gray-700' : 'text-gray-400'}`}>
                          {s.name}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="border-t border-gray-50 pt-1.5 flex justify-between text-[8px] text-gray-400 font-semibold">
                <span>B.Tech in Computer Science · NIT Warangal</span>
                <span>CGPA: 8.7</span>
              </div>
            </div>

            {/* OVERLAYS */}
            {/* ATS Score Gauge */}
            <div 
              className="absolute -top-5 -right-5 bg-white rounded-2xl p-3 shadow-xl border border-gray-100 flex items-center gap-3 transition-opacity duration-300 z-30"
              style={{ 
                transform: 'translateZ(45px)',
                opacity: isSpinning ? 0 : 1,
                boxShadow: '0 20px 40px -5px rgba(0,0,0,0.08)'
              }}
            >
              <div className="relative w-12 h-12 flex items-center justify-center shrink-0">
                <svg className="w-full h-full -rotate-90" viewBox="0 0 36 36">
                  <circle cx="18" cy="18" r="14" stroke="#F3F4F6" strokeWidth="3.5" fill="transparent" />
                  <circle 
                    cx="18" cy="18" r="14" stroke="#10B981" strokeWidth="3.5" fill="transparent"
                    strokeDasharray="85 100" strokeLinecap="round" 
                    style={{ filter: 'drop-shadow(0 0 2px rgba(16, 185, 129, 0.2))' }}
                  />
                </svg>
                <span className="absolute text-[10px] font-black text-gray-700">85%</span>
              </div>
              <div className="text-left">
                <div className="text-[10px] font-bold text-gray-800">ATS Match Score</div>
                <div className="text-[8px] font-bold text-emerald-500 bg-emerald-50 px-1.5 py-0.5 rounded-md mt-0.5">
                  Application at Google
                </div>
              </div>
            </div>

            {/* Stats list */}
            <div 
              className="absolute bottom-14 -left-8 bg-white rounded-xl shadow-xl border border-gray-100 p-3.5 w-32 z-30 transition-opacity duration-300"
              style={{ 
                transform: 'translateZ(30px) rotate(-1.5deg)',
                opacity: isSpinning ? 0 : 1,
                boxShadow: '0 15px 30px -5px rgba(0,0,0,0.06)'
              }}
            >
              <span className="text-[8px] font-bold uppercase tracking-wider text-gray-400 block mb-1.5 text-left">Matching Stats</span>
              <div className="space-y-1.5 text-left">
                <div className="flex justify-between text-[9px] font-semibold text-gray-650">
                  <span>Hard Skills</span>
                  <span className="text-emerald-500">6 / 8 match</span>
                </div>
                <div className="w-full h-1 bg-gray-100 rounded-full">
                  <div className="h-full bg-emerald-500 rounded-full w-[75%]" />
                </div>
                <div className="flex justify-between text-[9px] font-semibold text-gray-650 pt-1">
                  <span>Soft Skills</span>
                  <span className="text-emerald-500">4 / 4 match</span>
                </div>
                <div className="w-full h-1 bg-gray-100 rounded-full">
                  <div className="h-full bg-emerald-500 rounded-full w-full" />
                </div>
              </div>
            </div>
          </>
        )}

        {/* ── TEMPLATE 1: COLORS (Erin Schaefer Sidebar Template) ── */}
        {idx === 1 && (
          <>
            <div className="flex-1 flex text-left -m-5 overflow-hidden rounded-2xl">
              {/* Colored Sidebar (Resumebar Brand Navy Accent) */}
              <div className="w-[125px] bg-[#14213D] text-white p-4 flex flex-col justify-between">
                <div className="space-y-4">
                  <div className="w-11 h-11 rounded-full border-2 border-white/20 bg-white/10 overflow-hidden flex items-center justify-center text-[11px] font-black shadow-inner">
                    ES
                  </div>
                  <div>
                    <h3 className="font-extrabold text-[12px] leading-tight">Erin Schaefer</h3>
                    <div className="text-[8px] opacity-80 mt-0.5 font-medium uppercase tracking-wider">Project Manager</div>
                  </div>
                </div>
                
                <div className="space-y-1.5 text-[7px] opacity-75 font-semibold border-t border-white/15 pt-3">
                  <div>erin.sch@email.com</div>
                  <div>+1 (555) 019-2831</div>
                  <div>Toronto, Canada</div>
                </div>
              </div>

              {/* Main Body Column */}
              <div className="flex-1 p-4 flex flex-col justify-between bg-white bg-opacity-100">
                <div className="space-y-3">
                  <div>
                    <span className="text-[8px] font-extrabold uppercase tracking-widest text-[#FCA311] block">Summary</span>
                    <p className="text-[8.5px] text-gray-600 leading-normal mt-0.5">
                      Results-driven Project Manager with 6+ years driving execution of complex software projects. Skilled at standups and client budgets.
                    </p>
                  </div>

                  <div className="border-t border-gray-100 pt-2">
                    <span className="text-[8px] font-extrabold uppercase tracking-widest text-[#FCA311] block">Work Experience</span>
                    <div className="mt-1">
                      <div className="text-[9px] font-extrabold text-gray-800 leading-tight">Deloitte · Project Lead</div>
                      <div className="text-[7.5px] text-gray-400 font-medium mt-0.5">2021 - Present</div>
                      <ul className="list-disc list-inside text-[8px] text-gray-500 space-y-0.5 mt-1 leading-normal">
                        <li>Managed $2M client portfolios, achieving 98% satisfaction.</li>
                        <li>Facilitated sprints and release planning sessions.</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-1 border-t border-gray-100 pt-2">
                  {['Agile', 'Scrum', 'Budgeting'].map(t => (
                    <span key={t} className="text-[7.5px] font-bold text-[#FCA311] bg-amber-50 px-1.5 py-0.5 rounded border border-[#FCA311]/15">
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* OVERLAYS */}
            {/* Slanted Green Hired Badge */}
            <div 
              className="absolute -top-3 left-6 z-30 transition-opacity duration-300"
              style={{ 
                transform: 'translateZ(45px) rotate(-6deg)',
                opacity: isSpinning ? 0 : 1,
                boxShadow: '0 10px 20px rgba(16,185,129,0.2)'
              }}
            >
              <div className="bg-emerald-500 text-white text-[9.5px] font-black uppercase px-3.5 py-1.5 rounded-full tracking-wider">
                ✓ Hired!
              </div>
            </div>

            {/* Accent Color customizer swatches */}
            <div 
              className="absolute bottom-10 -right-8 bg-white rounded-2xl shadow-xl border border-gray-100 p-3.5 w-[140px] z-30 text-left transition-opacity duration-300"
              style={{ 
                transform: 'translateZ(35px)',
                opacity: isSpinning ? 0 : 1,
                boxShadow: '0 15px 30px -5px rgba(0,0,0,0.08)'
              }}
            >
              <span className="text-[8px] font-bold uppercase tracking-wider text-gray-400 block mb-1.5">Accent Color</span>
              <div className="grid grid-cols-5 gap-1.5 mb-1.5">
                {[
                  '#3B82F6', '#10B981', '#EF4444', '#8B5CF6', '#F59E0B',
                  '#6B7280', '#EC4899', '#0EA5E9', '#A78BFA', '#111827'
                ].map((c, i) => (
                  <div 
                    key={i} 
                    className="w-3.5 h-3.5 rounded-full flex items-center justify-center cursor-pointer border border-gray-100 transition-transform hover:scale-110"
                    style={{ 
                      background: c,
                      boxShadow: i === 3 ? '0 0 0 2px rgba(139, 92, 246, 0.4)' : 'none'
                    }}
                  >
                    {i === 3 && <span className="text-white" style={{ fontSize: 5 }}>✓</span>}
                  </div>
                ))}
              </div>
              <span className="text-[8.5px] font-bold text-[#FCA311] hover:underline cursor-pointer">
                Use custom color
              </span>
            </div>

            {/* Background pattern chooser */}
            <div 
              className="absolute top-10 -left-6 bg-white rounded-xl shadow-xl border border-gray-100 p-2.5 w-24 z-30 text-left transition-opacity duration-300"
              style={{ 
                transform: 'translateZ(25px) rotate(-1.5deg)',
                opacity: isSpinning ? 0 : 1,
                boxShadow: '0 15px 30px -5px rgba(0,0,0,0.06)'
              }}
            >
              <span className="text-[7.5px] font-bold uppercase tracking-wider text-gray-400 block mb-1.5">Background</span>
              <div className="grid grid-cols-3 gap-1">
                {['#F3F4F6', '#EEF2F6', '#EEF2F6', '#EEF2F6', '#EEF2F6', '#EEF2F6'].map((bg, i) => (
                  <div 
                    key={i} 
                    className="w-5.5 h-5.5 rounded flex items-center justify-center border border-gray-200 cursor-pointer"
                    style={{ 
                      background: i === 0 ? 'linear-gradient(135deg, #FCA311 0%, #E59200 100%)' : bg 
                    }}
                  >
                    {i === 0 && <span className="text-white text-[7px] font-bold">✓</span>}
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* ── TEMPLATE 2: TYPOGRAPHY (Emma Smith Serif Template) ── */}
        {idx === 2 && (
          <>
            <div className="flex-1 flex flex-col justify-between text-left" style={{ fontFamily: 'Georgia, serif' }}>
              {/* Header */}
              <div className="border-b border-gray-100 pb-2 text-center">
                <h3 className="text-[15px] font-bold text-gray-800 tracking-tight" style={{ fontFamily: 'Georgia, serif' }}>EMMA SMITH</h3>
                <div className="text-[8px] text-[#FCA311] font-bold uppercase tracking-widest mt-0.5" style={{ fontFamily: 'sans-serif' }}>
                  Scrum Master · Agile Practitioner
                </div>
              </div>

              {/* Body */}
              <div className="flex-1 mt-3 space-y-3">
                <div>
                  <span className="text-[8px] font-bold uppercase tracking-wider text-gray-400 block" style={{ fontFamily: 'sans-serif' }}>Profile Summary</span>
                  <p className="text-[8.5px] text-gray-600 leading-relaxed italic mt-0.5">
                    Agile leader with 5+ years driving efficiency across software development teams. Passionate about backlog health, velocity tracking, and mentoring engineers.
                  </p>
                </div>

                <div className="border-t border-gray-100 pt-2">
                  <span className="text-[8px] font-bold uppercase tracking-wider text-gray-400 block" style={{ fontFamily: 'sans-serif' }}>Experience</span>
                  <div className="mt-1">
                    <div className="flex justify-between items-center text-[9px] font-bold text-gray-805">
                      <span>Scrum Master at Spotify</span>
                      <span className="text-[7.5px] text-gray-400 font-medium" style={{ fontFamily: 'sans-serif' }}>2022 - Present</span>
                    </div>
                    <p className="text-[8.5px] text-gray-500 mt-1 leading-normal">
                      Guided 3 squads to streamline development pipelines, accelerating velocity by 25%. Coached Product Owners on backlog grooming.
                    </p>
                  </div>
                </div>
              </div>

              {/* Skills Row */}
              <div className="border-t border-gray-100 pt-2 flex flex-wrap gap-1" style={{ fontFamily: 'sans-serif' }}>
                {['Agile sprints', 'Jira Systems', 'Velocity metrics'].map(sk => (
                  <span key={sk} className="text-[7.5px] bg-gray-50 border border-gray-200 rounded px-1.5 py-0.5 text-gray-500 font-semibold">{sk}</span>
                ))}
              </div>
            </div>

            {/* OVERLAYS */}
            {/* Font list */}
            <div 
              className="absolute top-10 -left-6 bg-white rounded-xl shadow-xl border border-gray-100 p-3.5 w-28 z-30 text-left transition-opacity duration-300"
              style={{ 
                transform: 'translateZ(30px) rotate(-1deg)',
                opacity: isSpinning ? 0 : 1,
                boxShadow: '0 15px 30px -5px rgba(0,0,0,0.06)',
              }}
            >
              <span className="text-[8px] font-bold uppercase tracking-wider text-gray-400 block mb-2">Typography</span>
              <div className="space-y-1.5">
                {[
                  { name: 'Rubik' },
                  { name: 'Lato' },
                  { name: 'Raleway', active: true },
                  { name: 'Exo' },
                  { name: 'Chivo' }
                ].map((f) => (
                  <div key={f.name} className="flex items-center justify-between cursor-pointer py-0.5">
                    <span className={`text-[10px] ${f.active ? 'text-[#FCA311] font-bold' : 'text-gray-500 font-medium'}`}>
                      {f.name}
                    </span>
                    {f.active && <span className="text-[#FCA311] text-[8px] font-bold">◀</span>}
                  </div>
                ))}
              </div>
            </div>

            {/* Sliders */}
            <div 
              className="absolute bottom-16 -right-6 bg-white rounded-2xl shadow-xl border border-gray-100 p-3.5 w-[150px] z-30 text-left transition-opacity duration-300"
              style={{ 
                transform: 'translateZ(35px)',
                opacity: isSpinning ? 0 : 1,
                boxShadow: '0 15px 30px -5px rgba(0,0,0,0.08)',
              }}
            >
              <div className="mb-3">
                <span className="text-[8px] font-bold text-gray-500 uppercase tracking-wider">Page Margins: 1</span>
                <div className="flex items-center gap-1.5 mt-1">
                  <span className="text-[8px] text-gray-400 font-bold">−</span>
                  <div className="flex-1 h-1 bg-gray-100 rounded-full relative">
                    <div className="absolute left-0 top-0 h-full w-[35%] bg-[#FCA311] rounded-full" />
                    <div className="absolute w-2.5 h-2.5 bg-[#FCA311] rounded-full shadow top-1/2 -translate-y-1/2 cursor-pointer" style={{ left: '32%' }} />
                  </div>
                  <span className="text-[8px] text-gray-400 font-bold">+</span>
                </div>
              </div>

              <div>
                <span className="text-[8px] font-bold text-gray-500 uppercase tracking-wider">Spacing: 3</span>
                <div className="flex items-center gap-1.5 mt-1">
                  <span className="text-[8px] text-gray-400 font-bold">−</span>
                  <div className="flex-1 h-1 bg-gray-100 rounded-full relative">
                    <div className="absolute left-0 top-0 h-full w-[58%] bg-[#FCA311] rounded-full" />
                    <div className="absolute w-2.5 h-2.5 bg-[#FCA311] rounded-full shadow top-1/2 -translate-y-1/2 cursor-pointer" style={{ left: '55%' }} />
                  </div>
                  <span className="text-[8px] text-gray-400 font-bold">+</span>
                </div>
              </div>
            </div>
          </>
        )}

        {/* ── TEMPLATE 3: AI WRITER (Ethan Smith Highlights) ── */}
        {idx === 3 && (
          <>
            <div className="flex-1 flex flex-col justify-between text-left">
              {/* Header */}
              <div className="border-b border-gray-100 pb-2 flex justify-between items-center">
                <div>
                  <h3 className="text-[13px] font-black text-gray-800 tracking-tight">Ethan Smith</h3>
                  <div className="text-[8px] text-gray-400 font-bold uppercase tracking-wider mt-0.5">Technical Product Manager</div>
                </div>
                <div className="text-[7.5px] text-right text-gray-400 font-medium">
                  <div>San Francisco, CA</div>
                  <div className="text-[#FCA311] font-bold">ethan.sm@email.com</div>
                </div>
              </div>

              {/* Body */}
              <div className="flex-1 mt-3 space-y-3">
                <div>
                  <span className="text-[8px] font-extrabold uppercase tracking-widest text-[#FCA311]">Professional Summary</span>
                  <div className="p-2 rounded-lg bg-amber-50/40 border border-amber-200 relative overflow-hidden mt-0.5 shadow-inner">
                    <div className="absolute top-0 left-0 h-full w-[3px] bg-[#FCA311]" />
                    <p className="text-[9px] text-gray-750 leading-relaxed font-semibold pl-1 animate-pulse">
                      ✦ Led launch of AI-driven feature recommendations, increasing click-through rates by 35% and user engagement by 20% across key verticals.
                    </p>
                  </div>
                </div>

                <div className="border-t border-gray-100 pt-2">
                  <span className="text-[8px] font-extrabold uppercase tracking-widest text-[#FCA311]">Work Experience</span>
                  <div className="mt-1">
                    <div className="flex justify-between items-center text-[9px] font-extrabold text-gray-850">
                      <span>Product Manager · Google</span>
                      <span className="text-gray-400 font-medium text-[8px]">2021 - Present</span>
                    </div>
                    <p className="text-[8.5px] text-gray-650 mt-1 leading-normal">
                      Managed sprints and maps for a team of 12 backend engineers. Drove integration of custom search pipelines.
                    </p>
                  </div>
                </div>
              </div>

              <div className="h-1 bg-gray-50 rounded w-full mt-2" />
            </div>

            {/* OVERLAYS */}
            {/* AI Assistant dialogue */}
            <div 
              className="absolute top-1/2 left-1/2 bg-white rounded-2xl p-4 shadow-2xl border border-amber-100 w-[240px] flex flex-col gap-2.5 z-30 text-left transition-opacity duration-300"
              style={{ 
                transform: 'translate3d(-50%, -50%, 55px)',
                opacity: isSpinning ? 0 : 1,
                boxShadow: '0 25px 50px -10px rgba(252, 163, 17, 0.25)'
              }}
            >
              <div className="flex items-center gap-1.5">
                <span className="text-brand-gz-purple text-[10px]">✦</span>
                <span className="text-[9.5px] font-bold text-gray-800 uppercase tracking-wider">AI Assistant</span>
              </div>

              <div className="space-y-1.5">
                <button className="w-full text-left bg-gradient-to-r from-[#FCA311] to-[#E59200] hover:from-[#E59200] hover:to-[#C97A1F] text-[#14213D] text-[9.5px] font-semibold py-1.5 px-3 rounded-lg flex items-center justify-between transition-colors shadow-sm">
                  <span>✦ Generate Skills from Job</span>
                  <span>→</span>
                </button>
                <button className="w-full text-left bg-gray-50 hover:bg-gray-100 border border-gray-200 text-gray-600 text-[9.5px] font-semibold py-1.5 px-3 rounded-lg flex items-center justify-between transition-colors">
                  <span>✦ Inspire Me</span>
                  <span>→</span>
                </button>
              </div>

              <div className="border-t border-gray-100 pt-2 flex items-center gap-2">
                <input 
                  type="text" 
                  placeholder="Or enter a custom request..." 
                  className="w-full text-[8.5px] bg-gray-50 rounded-lg py-1.5 px-2.5 border border-gray-100 focus:outline-none pointer-events-none"
                  readOnly 
                />
                <button className="w-5 h-5 rounded-lg bg-[#FCA311] flex items-center justify-center text-[#14213D] text-[8px] font-bold shrink-0">
                  ↑
                </button>
              </div>
            </div>

            {/* Bottom utility bar */}
            <div 
              className="absolute -bottom-4 left-4 right-4 bg-gray-900 rounded-xl p-2 flex items-center justify-between shadow-xl z-30 transition-opacity duration-300"
              style={{ 
                transform: 'translateZ(25px)',
                opacity: isSpinning ? 0 : 1,
                boxShadow: '0 10px 20px rgba(20, 33, 61, 0.3)'
              }}
            >
              <div className="flex items-center gap-1.5">
                <button className="bg-[#FCA311] hover:bg-[#E59200] text-[#14213D] text-[8.5px] font-bold py-1 px-2.5 rounded-md flex items-center gap-0.5 transition-colors">
                  <span className="font-bold">+</span> New entry
                </button>
                <button className="bg-white/10 hover:bg-white/15 text-white/90 text-[8.5px] font-semibold py-1 px-2 rounded-md transition-colors">
                  New Group
                </button>
              </div>
              <div className="flex items-center gap-2 pr-1">
                <span className="text-[9px] text-white/60">A</span>
                <div className="w-3.5 h-3.5 rounded-full bg-[#FCA311] border border-white/20" />
                <svg className="w-3.5 h-3.5 text-white/50" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h10M4 18h16" />
                </svg>
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <div className="w-full flex flex-col items-center">
      {/* 3D Scene Wrapper - Reverted to original max-w-[440px] h-[525px] */}
      <div 
        className="relative w-[440px] h-[525px] select-none"
        style={{ perspective: '1600px' }}
      >
        
        {/* ─── 3D ROTATING CONTAINER ─── */}
        <div
          className="w-full h-full relative transition-transform ease-out"
          style={{
            transformStyle: 'preserve-3d',
            transitionDuration: `${SPIN_MS}ms`,
            // Base 3D tilt combined with the dynamic Y-rotation (adds 180° on each transition)
            transform: `rotateY(${rotation - 14}deg) rotateX(8deg) rotateZ(-1.5deg)`,
          }}
        >
          
          {/* ────────────────────────────────────────────────────────
              FRONT CARD (Visible when facing forward)
              ──────────────────────────────────────────────────────── */}
          <div 
            className="absolute inset-0 w-full h-full bg-white rounded-2xl border border-gray-100 shadow-2xl p-5 flex flex-col justify-between"
            style={{
              backfaceVisibility: 'hidden',
              WebkitBackfaceVisibility: 'hidden',
              transformStyle: 'preserve-3d',
              transform: 'rotateY(0deg)',
              boxShadow: '0 25px 65px -15px rgba(0, 0, 0, 0.08), 0 0 40px rgba(252, 163, 17, 0.04)',
            }}
          >
            {renderCardContent(frontIdx)}
          </div>

          {/* ────────────────────────────────────────────────────────
              BACK CARD (Visible when rotated 180deg)
              ──────────────────────────────────────────────────────── */}
          <div 
            className="absolute inset-0 w-full h-full bg-white rounded-2xl border border-gray-100 shadow-2xl p-5 flex flex-col justify-between"
            style={{
              backfaceVisibility: 'hidden',
              WebkitBackfaceVisibility: 'hidden',
              transformStyle: 'preserve-3d',
              transform: 'rotateY(180deg)',
              boxShadow: '0 25px 65px -15px rgba(0, 0, 0, 0.08), 0 0 40px rgba(252, 163, 17, 0.04)',
            }}
          >
            {renderCardContent(backIdx)}
          </div>

        </div>
      </div>

      {/* ── DOT INDICATORS ── */}
      <div className="flex items-center gap-2 mt-8">
        {[
          { label: 'ATS Check', idx: 0 },
          { label: 'Colors & Bgs', idx: 1 },
          { label: 'Fonts & Margins', idx: 2 },
          { label: 'AI Writer', idx: 3 }
        ].map((item) => (
          <button
            key={item.idx}
            onClick={() => handleDotClick(item.idx)}
            className={`h-2.5 rounded-full transition-all duration-300 ${
              item.idx === activeIdx 
                ? 'w-7 bg-[#FCA311] shadow-sm shadow-amber-500/20' 
                : 'w-2.5 bg-gray-200 hover:bg-gray-300'
            }`}
            title={item.label}
            aria-label={`Show ${item.label}`}
          />
        ))}
      </div>
      
      {/* Dynamic subtitle text under indicators */}
      <div className="h-4 mt-2">
        <span className="text-[10px] font-bold uppercase tracking-widest text-[#FCA311] transition-all duration-300">
          {[
            '01. Tailor your resume dynamically to matches',
            '02. Customize colors & select premium themes',
            '03. Tweak spacing & select beautiful typography',
            '04. Let AI generate professional sections'
          ][activeIdx]}
        </span>
      </div>
    </div>
  );
}
